/**
 * Marketing Automation Types
 *
 * Comprehensive TypeScript types for marketing automation tools
 * supporting private lender marketing workflows.
 *
 * Covers lead scoring, drip sequences, automation triggers,
 * conversion funnels, attribution tracking, lead magnets,
 * and dashboard metrics.
 *
 * @see Issue #638 - Marketing Automation Tools
 */

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Lead scoring tier classification.
 *
 * Tiers are determined by cumulative point scores from
 * property data, borrower profile, deal viability, and engagement factors.
 */
export enum LeadScoringTier {
  /** 75+ points - High-intent lead ready for immediate outreach */
  HOT = 'HOT',
  /** 50-74 points - Engaged lead requiring nurture follow-up */
  WARM = 'WARM',
  /** 25-49 points - Early-stage lead needing further qualification */
  COOL = 'COOL',
  /** 0-24 points - Unqualified or disengaged lead */
  COLD = 'COLD',
}

/**
 * Conversion funnel stages for private lending marketing.
 *
 * Tracks a lead from initial website visit through to funded deal,
 * aligned with the typical private/hard money lending lifecycle.
 */
export enum ConversionFunnelStage {
  /** Initial website or landing page visit */
  VISIT = 'VISIT',
  /** Lead captured via form, chat, or lead magnet download */
  LEAD_CAPTURE = 'LEAD_CAPTURE',
  /** Borrower started the loan intake process */
  INTAKE_STARTED = 'INTAKE_STARTED',
  /** Borrower completed the full intake submission */
  INTAKE_COMPLETED = 'INTAKE_COMPLETED',
  /** Case created in the system from the intake */
  CASE_CREATED = 'CASE_CREATED',
  /** Case is in underwriting review */
  UNDERWRITING = 'UNDERWRITING',
  /** Loan approved by underwriting */
  APPROVED = 'APPROVED',
  /** Loan funded and closed */
  FUNDED = 'FUNDED',
}

// =============================================================================
// LEAD SCORING
// =============================================================================

/**
 * A named sub-factor within a lead scoring category.
 *
 * Each sub-factor contributes a configurable number of points
 * toward the total score for its parent category.
 */
export interface LeadScoringSubFactor {
  /** Human-readable name of the sub-factor (e.g., "Property Type Match") */
  name: string;
  /** Point value assigned when this sub-factor is satisfied */
  points: number;
  /** Optional description explaining the scoring criteria */
  description?: string;
}

/**
 * A scoring factor category with its constituent sub-factors.
 *
 * The maximum points for any single category is 25, ensuring
 * balanced scoring across all four dimensions.
 */
export interface LeadScoringFactor {
  /** Maximum points available in this category (0-25) */
  maxPoints: number;
  /** Individual sub-factors that contribute to this category's score */
  subFactors: LeadScoringSubFactor[];
}

/**
 * Configuration for a lead scoring tier threshold.
 *
 * Defines what happens when a lead reaches a specific tier,
 * including team notifications and auto-assignment behavior.
 */
export interface LeadScoringTierConfig {
  /** The tier classification */
  tier: LeadScoringTier;
  /** Minimum score required to enter this tier */
  minScore: number;
  /** Display label for the tier (e.g., "Hot Lead") */
  label: string;
  /** Whether to auto-assign the lead to a loan officer */
  autoAssign: boolean;
  /** Whether to send a notification to the sales team */
  notifyTeam: boolean;
}

/**
 * Complete lead scoring configuration.
 *
 * Defines four balanced scoring categories (25 points each, 100 total)
 * and tier thresholds that determine how leads are classified and routed.
 *
 * @example
 * ```typescript
 * const config: LeadScoringConfig = {
 *   factors: {
 *     propertyData: {
 *       maxPoints: 25,
 *       subFactors: [
 *         { name: 'Property Type Match', points: 10 },
 *         { name: 'Location in Target Market', points: 10 },
 *         { name: 'ARV Provided', points: 5 },
 *       ],
 *     },
 *     borrowerProfile: { maxPoints: 25, subFactors: [...] },
 *     dealViability: { maxPoints: 25, subFactors: [...] },
 *     engagement: { maxPoints: 25, subFactors: [...] },
 *   },
 *   tiers: [
 *     { tier: LeadScoringTier.HOT, minScore: 75, label: 'Hot Lead', autoAssign: true, notifyTeam: true },
 *     { tier: LeadScoringTier.WARM, minScore: 50, label: 'Warm Lead', autoAssign: false, notifyTeam: true },
 *     { tier: LeadScoringTier.COOL, minScore: 25, label: 'Cool Lead', autoAssign: false, notifyTeam: false },
 *     { tier: LeadScoringTier.COLD, minScore: 0, label: 'Cold Lead', autoAssign: false, notifyTeam: false },
 *   ],
 * };
 * ```
 */
export interface LeadScoringConfig {
  /** Scoring factor categories (each max 25 points, 100 total) */
  factors: {
    /** Property-related scoring (type, location, value) - max 25 points */
    propertyData: LeadScoringFactor;
    /** Borrower qualification scoring (experience, credit, liquidity) - max 25 points */
    borrowerProfile: LeadScoringFactor;
    /** Deal financial viability scoring (LTV, ARV, exit strategy) - max 25 points */
    dealViability: LeadScoringFactor;
    /** Lead engagement scoring (email opens, site visits, form fills) - max 25 points */
    engagement: LeadScoringFactor;
  };
  /** Tier threshold definitions, ordered from highest to lowest */
  tiers: LeadScoringTierConfig[];
}

// =============================================================================
// DRIP SEQUENCES
// =============================================================================

/**
 * Trigger configuration for starting a drip sequence.
 *
 * Defines the event that initiates the sequence and any
 * additional conditions that must be met.
 */
export interface TriggerConfig {
  /** Event name that triggers the sequence (e.g., "lead_captured", "intake_started") */
  event: string;
  /** Additional conditions that must be met for the trigger to fire */
  conditions: Record<string, string | number | boolean>;
}

/**
 * A single step within a drip sequence.
 *
 * Each step defines a timed action (email, SMS, or task)
 * that executes after a configurable delay.
 */
export interface DripStep {
  /** Numeric delay before this step executes */
  delay: number;
  /** Unit of the delay value */
  delayUnit: 'hours' | 'days';
  /** Communication channel or action type */
  type: 'email' | 'sms' | 'task';
  /** Template identifier used to render the content */
  template: string;
  /** Subject line (applicable for email type) */
  subject?: string;
  /** Body content or task description */
  body?: string;
}

/**
 * A reusable drip sequence template.
 *
 * Defines an automated series of timed communications triggered
 * by a specific event, with configurable exit conditions.
 *
 * @example
 * ```typescript
 * const sequence: DripSequenceTemplate = {
 *   id: 'seq-new-lead-nurture',
 *   name: 'New Lead Nurture',
 *   description: 'Automated follow-up for newly captured leads',
 *   trigger: {
 *     event: 'lead_captured',
 *     conditions: { source: 'website' },
 *   },
 *   steps: [
 *     { delay: 0, delayUnit: 'hours', type: 'email', template: 'welcome', subject: 'Welcome!' },
 *     { delay: 2, delayUnit: 'days', type: 'email', template: 'case-study', subject: 'See how we helped...' },
 *     { delay: 5, delayUnit: 'days', type: 'task', template: 'call-follow-up', body: 'Call lead to discuss needs' },
 *   ],
 *   exitConditions: ['case_created', 'unsubscribed', 'replied'],
 * };
 * ```
 */
export interface DripSequenceTemplate {
  /** Unique identifier for the sequence template */
  id: string;
  /** Human-readable sequence name */
  name: string;
  /** Description of the sequence's purpose and strategy */
  description: string;
  /** Event and conditions that start this sequence */
  trigger: TriggerConfig;
  /** Ordered list of timed steps in the sequence */
  steps: DripStep[];
  /** Events that cause a contact to exit the sequence early */
  exitConditions: string[];
}

// =============================================================================
// AUTOMATION CONFIG
// =============================================================================

/**
 * Notification configuration for automation triggers.
 *
 * Defines who gets notified and how when an automation fires.
 */
export interface AutomationNotification {
  /** Notification delivery channel */
  channel: 'email' | 'sms' | 'in_app' | 'slack';
  /** Target recipients (user IDs, team names, or email addresses) */
  recipients: string[];
  /** Notification message template */
  messageTemplate: string;
}

/**
 * A single marketing automation trigger rule.
 *
 * Maps an event to an automated action, with optional
 * conditions, delay, and notification settings.
 *
 * Named `MarketingAutomationTrigger` to distinguish from
 * pipeline `AutomationTrigger` in seed/domains/pipelines.
 */
export interface MarketingAutomationTrigger {
  /** Event name that activates this automation */
  event: string;
  /** Action to perform when the event fires */
  action: 'enroll_sequence' | 'notify_team' | 'update_field' | 'create_task';
  /** Drip sequence ID to enroll in (required when action is 'enroll_sequence') */
  sequence?: string;
  /** Additional conditions for the trigger to fire */
  conditions?: Record<string, string | number | boolean>;
  /** Delay in minutes before executing the action */
  delay?: number;
  /** Notification config (required when action is 'notify_team') */
  notification?: AutomationNotification;
}

/**
 * Complete automation configuration.
 *
 * Defines a set of event-driven automation rules that
 * execute actions based on lead and case lifecycle events.
 *
 * @example
 * ```typescript
 * const config: AutomationConfig = {
 *   triggers: [
 *     {
 *       event: 'lead_captured',
 *       action: 'enroll_sequence',
 *       sequence: 'seq-new-lead-nurture',
 *       conditions: { tier: 'WARM' },
 *     },
 *     {
 *       event: 'lead_scored_hot',
 *       action: 'notify_team',
 *       notification: {
 *         channel: 'slack',
 *         recipients: ['#sales-alerts'],
 *         messageTemplate: 'Hot lead: {{lead_name}} scored {{score}} points',
 *       },
 *     },
 *     {
 *       event: 'intake_completed',
 *       action: 'create_task',
 *       delay: 30,
 *     },
 *   ],
 * };
 * ```
 */
export interface AutomationConfig {
  /** List of automation trigger rules */
  triggers: MarketingAutomationTrigger[];
}

// =============================================================================
// MARKETING ATTRIBUTION
// =============================================================================

/**
 * Marketing attribution data for tracking lead sources.
 *
 * Captures UTM parameters, referral information, and touchpoint
 * timestamps for multi-touch attribution analysis.
 *
 * @example
 * ```typescript
 * const attribution: MarketingAttribution = {
 *   utmSource: 'google',
 *   utmMedium: 'cpc',
 *   utmCampaign: 'bridge-loans-q1-2026',
 *   landingPage: '/bridge-loans',
 *   firstTouchAt: new Date('2026-01-15'),
 *   lastTouchAt: new Date('2026-02-01'),
 * };
 * ```
 */
export interface MarketingAttribution {
  /** Traffic source (e.g., "google", "facebook", "partner-site") */
  utmSource?: string;
  /** Marketing medium (e.g., "cpc", "email", "organic", "referral") */
  utmMedium?: string;
  /** Campaign name (e.g., "bridge-loans-q1-2026") */
  utmCampaign?: string;
  /** Paid search keyword or term */
  utmTerm?: string;
  /** Content variant identifier for A/B testing */
  utmContent?: string;
  /** Referral partner code */
  referralCode?: string;
  /** Referral source name (e.g., "broker-network", "past-borrower") */
  referralSource?: string;
  /** Landing page path where the lead first arrived */
  landingPage?: string;
  /** Timestamp of the first marketing touchpoint */
  firstTouchAt?: Date;
  /** Timestamp of the most recent marketing touchpoint */
  lastTouchAt?: Date;
}

// =============================================================================
// LEAD MAGNETS
// =============================================================================

/**
 * Configuration for a downloadable lead magnet.
 *
 * Lead magnets are gated content pieces used to capture
 * lead information in exchange for valuable resources.
 *
 * @example
 * ```typescript
 * const leadMagnet: LeadMagnetConfig = {
 *   id: 'lm-bridge-loan-guide',
 *   name: 'Bridge Loan Rate Guide',
 *   type: 'pdf',
 *   sourceType: 'auto_generated',
 *   dataSource: 'current_rate_sheet',
 *   description: 'Current bridge loan rates and terms by property type and LTV',
 * };
 * ```
 */
export interface LeadMagnetConfig {
  /** Unique identifier for the lead magnet */
  id: string;
  /** Display name of the lead magnet */
  name: string;
  /** File format of the downloadable content */
  type: 'pdf' | 'excel' | 'report';
  /** How the content is produced */
  sourceType: 'static' | 'auto_generated' | 'ai_generated';
  /** Data source identifier for auto-generated or AI-generated content */
  dataSource?: string;
  /** Description shown to the lead before download */
  description: string;
}

// =============================================================================
// DASHBOARD METRICS
// =============================================================================

/**
 * Funnel stage count for dashboard visualization.
 *
 * Represents the number of leads at a specific conversion stage.
 */
export interface FunnelStageData {
  /** Conversion funnel stage */
  stage: ConversionFunnelStage;
  /** Number of leads currently at this stage */
  count: number;
}

/**
 * Lead quality distribution across scoring tiers.
 *
 * Provides a count of leads in each scoring tier
 * for quality assessment and pipeline health checks.
 */
export interface LeadQualityBreakdown {
  /** Number of hot leads (75+ points) */
  hot: number;
  /** Number of warm leads (50-74 points) */
  warm: number;
  /** Number of cool leads (25-49 points) */
  cool: number;
  /** Number of cold leads (0-24 points) */
  cold: number;
}

/**
 * Performance metrics for a single drip sequence campaign.
 *
 * Tracks engagement and conversion rates for optimization.
 */
export interface CampaignPerformanceMetrics {
  /** Drip sequence ID or campaign identifier */
  sequenceId: string;
  /** Human-readable sequence or campaign name */
  sequenceName: string;
  /** Number of email opens */
  opens: number;
  /** Number of link clicks */
  clicks: number;
  /** Number of conversions (e.g., intake started, case created) */
  conversions: number;
}

/**
 * Attribution source with funded deal count.
 *
 * Maps a traffic source to its funded loan outcome
 * for ROI analysis.
 */
export interface TopSourceMetric {
  /** Traffic source identifier (e.g., "google_cpc", "referral_broker") */
  source: string;
  /** Number of funded deals attributed to this source */
  fundedCount: number;
}

/**
 * Pipeline value breakdown by marketing source.
 *
 * Tracks the total loan amount in the pipeline
 * attributed to each marketing source.
 */
export interface PipelineValueBySource {
  /** Traffic source identifier */
  source: string;
  /** Total pipeline value in dollars attributed to this source */
  pipelineValue: number;
}

/**
 * Weekly marketing activity statistics.
 *
 * Provides period-over-period comparison data
 * for the marketing dashboard.
 */
export interface WeeklyMarketingStats {
  /** ISO week start date (YYYY-MM-DD) */
  weekStart: string;
  /** New leads captured during the week */
  newLeads: number;
  /** Emails sent during the week */
  emailsSent: number;
  /** Intakes completed during the week */
  intakesCompleted: number;
  /** Cases created during the week */
  casesCreated: number;
  /** Loans funded during the week */
  loansFunded: number;
}

/**
 * Complete marketing dashboard metrics payload.
 *
 * Aggregates all marketing KPIs for dashboard rendering,
 * including funnel visualization, lead quality, campaign
 * performance, attribution, and weekly trends.
 *
 * @example
 * ```typescript
 * const metrics: MarketingDashboardMetrics = {
 *   funnelData: [
 *     { stage: ConversionFunnelStage.VISIT, count: 5000 },
 *     { stage: ConversionFunnelStage.LEAD_CAPTURE, count: 500 },
 *     { stage: ConversionFunnelStage.INTAKE_STARTED, count: 200 },
 *     { stage: ConversionFunnelStage.FUNDED, count: 25 },
 *   ],
 *   leadQuality: { hot: 45, warm: 120, cool: 200, cold: 135 },
 *   campaignPerformance: [...],
 *   topSources: [...],
 *   pipelineValueBySource: [...],
 *   weeklyStats: [...],
 * };
 * ```
 */
export interface MarketingDashboardMetrics {
  /** Lead counts at each conversion funnel stage */
  funnelData: FunnelStageData[];
  /** Lead distribution across scoring tiers */
  leadQuality: LeadQualityBreakdown;
  /** Performance metrics per drip sequence or campaign */
  campaignPerformance: CampaignPerformanceMetrics[];
  /** Top traffic sources ranked by funded deal count */
  topSources: TopSourceMetric[];
  /** Pipeline dollar value attributed to each source */
  pipelineValueBySource: PipelineValueBySource[];
  /** Weekly marketing activity stats for trend analysis */
  weeklyStats: WeeklyMarketingStats[];
}
